import "./App.css";

import { store } from "./redux/store";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import AppRoutes from "routes/AppRoutes";
import Toaster from "components/atoms/Toaster";
import Header from "components/templates/Header";

import { createTheme } from "@mui/material";

import createCache from "@emotion/cache";
import { CssBaseline } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { CacheProvider } from "@emotion/react";
import { LocalizationProvider } from "@mui/lab";
import dateAdapter from "@mui/lab/AdapterDateFns";
// import { ScopedCssBaseline } from "@mui/material";
import AppModal from "components/templates/AppModal";
import { ThemeProvider } from "@mui/material/styles";
import ErrorBoundary from "components/atoms/ErrorBoundary";
import RegisterAppDispatch from "components/atoms/RegisterAppDispatch";
import ScrollToTop from "components/atoms/ScrollToTop";
import Footer from "components/templates/Footer";
import { useTranslation } from "react-i18next";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { useEffect } from "react";
// import theme from "theme";

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      success: object;
      warning: object;
    };
  }
  interface ThemeOptions {
    status?: {
      success?: object;
      warning?: object;
    };
  }
}

const cache = createCache({ key: "css", prepend: true });

const cacheRtl = createCache({
  key: "muirtl",
  //@ts-ignore
  stylisPlugins: [prefixer, rtlPlugin],
});
export default function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const isRTL = i18n.language === "ar";

    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const theme = createTheme({
    status: {
      success: {
        color: "rgba(84, 214, 44, 0.16)",
        textContrast: "rgb(34, 154, 22)",
      },
      warning: {
        color: "rgba(255, 193, 7, 0.16)",
        textContrast: "rgb(183, 129, 3)",
      },
    },
    palette: {
      primary: {
        light: "#7B3FA3",
        main: "#520A76",
        dark: "#3D0859",
      },
      secondary: {
        light: "#fffadd",
        main: "#f5d312",
        dark: "#e7c400",
      },
      success: {
        main: "rgb(34, 154, 22)",
        light: "rgba(84, 214, 44, 0.16)",
        dark: "#1b5e20",
      },
      warning: {
        main: "rgb(183, 129, 3)",
        light: "rgba(255, 193, 7, 0.16)",
        dark: "#e65100",
      },
      error: {
        main: "rgb(183, 33, 54)",
        light: "rgba(255, 72, 66, 0.16)",
        dark: "#c62828",
      },
      info: {
        main: "rgb(2, 136, 209)",
        light: "rgb(2, 136, 209, 0.16)",
        dark: "#01579b",
      },
    },
    typography: {
      fontFamily: "arabic, sans-serif",
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          maxWidthXl: {
            maxWidth: "1280px !important",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 10,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 10,
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(82, 10, 118, 0.5)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: "#520A76",
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
    },
  });

  return (
    <ErrorBoundary>
      <SnackbarProvider
        maxSnack={5}
        hideIconVariant
        preventDuplicate
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        iconVariant={{
          success: "✅",
          error: "✖️",
          warning: "⚠️",
          info: "ℹ️",
        }}
      >
        <Toaster />
        <ErrorBoundary>
          <CacheProvider value={i18n.language === "ar" ? cacheRtl : cache}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={dateAdapter}>
                <CssBaseline />
                {/* <ScopedCssBaseline> */}
                <Provider store={store}>
                  <RegisterAppDispatch />
                  <BrowserRouter>
                    <ScrollToTop />
                    <ErrorBoundary>
                      <Header />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <AppModal />
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <AppRoutes />
                    </ErrorBoundary>
                    <Footer />
                  </BrowserRouter>
                </Provider>
                {/* </ScopedCssBaseline> */}
              </LocalizationProvider>
            </ThemeProvider>
          </CacheProvider>
        </ErrorBoundary>
      </SnackbarProvider>
    </ErrorBoundary>
  );
}
